import type {
  BulkApprovalResponse,
  BulkExpectedStatus,
  BulkStatusMismatch,
} from "../../../../api/approvalRequests.service.js";

export const APPROVAL_STATUS_TARGETS = ["APPROVED", "REJECTED", "SUSPENDED"] as const;

export type ApprovalStatusTarget = (typeof APPROVAL_STATUS_TARGETS)[number];

export type ApprovalRequestStatusRow = {
  approvalRequestId: string;
  status: ApprovalStatusTarget;
};

export const APPROVAL_WRITE_PERMISSION = "staff.approval.write";

export const APPROVAL_STATUS_MAX_IDS_PER_CALL = 50;

export const APPROVAL_STATUS_EXPECTED: BulkExpectedStatus = "NEW";

export const APPROVAL_STATUS_BUCKETS = [
  "decided",
  "locked",
  "notFound",
  "invalidStatus",
  "alreadyInTarget",
  "callbackFailed",
] as const;

export type ApprovalStatusBucket = (typeof APPROVAL_STATUS_BUCKETS)[number];

export type ApprovalStatusBuckets = Record<ApprovalStatusBucket, string[]>;

export type ApprovalStatusChunk = { status: ApprovalStatusTarget; ids: string[] };

export const EMPTY_BUCKETS: ApprovalStatusBuckets = {
  decided: [],
  locked: [],
  notFound: [],
  invalidStatus: [],
  alreadyInTarget: [],
  callbackFailed: [],
};

export function countByStatus(
  rows: ApprovalRequestStatusRow[],
): Record<ApprovalStatusTarget, number> {
  const counts = { APPROVED: 0, REJECTED: 0, SUSPENDED: 0 };
  for (const { status } of rows) counts[status] += 1;
  return counts;
}

export function planApprovalStatusChunks(
  rows: ApprovalRequestStatusRow[],
  chunkSize: number,
): ApprovalStatusChunk[] {
  return APPROVAL_STATUS_TARGETS.flatMap(status => {
    const ids = rows.filter(row => row.status === status).map(row => row.approvalRequestId);
    const chunks: ApprovalStatusChunk[] = [];
    for (let from = 0; from < ids.length; from += chunkSize) {
      chunks.push({ status, ids: ids.slice(from, from + chunkSize) });
    }
    return chunks;
  });
}

function alreadyInTargetIds(status: ApprovalStatusTarget, result: BulkApprovalResponse): string[] {
  switch (status) {
    case "APPROVED":
      return result.alreadyApprovedIds ?? [];
    case "REJECTED":
      return result.alreadyRejectedIds ?? [];
    case "SUSPENDED":
      return result.alreadySuspendedIds ?? [];
  }
}

export function bucketsOf(
  status: ApprovalStatusTarget,
  result: BulkApprovalResponse,
): ApprovalStatusBuckets {
  return {
    decided: result.updated.map(item => item.id),
    locked: result.lockedIds,
    notFound: result.notFoundIds,
    invalidStatus: result.invalidStatusIds,
    alreadyInTarget: alreadyInTargetIds(status, result),
    callbackFailed: result.callbackFailedIds ?? [],
  };
}

export function mergeBuckets(
  previous: ApprovalStatusBuckets,
  incoming: ApprovalStatusBuckets,
): ApprovalStatusBuckets {
  const merged = { ...EMPTY_BUCKETS };
  for (const bucket of APPROVAL_STATUS_BUCKETS) {
    merged[bucket] = [...previous[bucket], ...incoming[bucket]];
  }
  return merged;
}

export type ApprovalStatusRunTotals = {
  buckets: ApprovalStatusBuckets;
  mismatches: BulkStatusMismatch[];
  unknown: ApprovalRequestStatusRow[];
  guardUnconfirmed: boolean;
};

export const EMPTY_TOTALS: ApprovalStatusRunTotals = {
  buckets: EMPTY_BUCKETS,
  mismatches: [],
  unknown: [],
  guardUnconfirmed: false,
};

export function totalsOf(
  status: ApprovalStatusTarget,
  result: BulkApprovalResponse,
): ApprovalStatusRunTotals {
  return {
    buckets: bucketsOf(status, result),
    mismatches: result.statusMismatches ?? [],
    unknown: [],
    guardUnconfirmed: result.statusMismatches === undefined,
  };
}

export function mergeTotals(
  previous: ApprovalStatusRunTotals,
  incoming: ApprovalStatusRunTotals,
): ApprovalStatusRunTotals {
  return {
    buckets: mergeBuckets(previous.buckets, incoming.buckets),
    mismatches: [...previous.mismatches, ...incoming.mismatches],
    unknown: [...previous.unknown, ...incoming.unknown],
    guardUnconfirmed: previous.guardUnconfirmed || incoming.guardUnconfirmed,
  };
}

export function withUnknownChunk(
  totals: ApprovalStatusRunTotals,
  chunk: ApprovalStatusChunk,
): ApprovalStatusRunTotals {
  const rows = chunk.ids.map(id => ({ approvalRequestId: id, status: chunk.status }));
  return { ...totals, unknown: [...totals.unknown, ...rows] };
}
